import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';

// Analytics
import { environment } from 'src/environments/environment';

import { DevelopmentPreview, Participations } from '@app/models';
import { UserService } from '../user/user.service';
import { ANALYTICS_DEVICE, HEADER_DONT_SAVE, HEADER_DONT_SAVE_REQ, HEADER_RESET } from '@app/constants';
import { DevelopmentInfo, DevelopmentParticipations, Resale } from '@interfaces/app';
import { GpsV2Service } from '../gps-v2/gps-v2.service';

@Injectable({
   providedIn: 'root'
})
export class DevelopmentService {

   textSelected: any
   optionSelected: any

   cacheDev: { [req: string]: {} } = {};

   constructor(private http: HttpClient, private userServ: UserService, private gpsServ: GpsV2Service, private toastrServ: ToastrService,) { }

   getDevelopmentInfo(pid: string) {
      return this.http.get<DevelopmentInfo>(`${environment.apiUrlBase}/project/info/${pid}`, this.cacheDev[`dev${pid}`] || HEADER_RESET)
         .pipe(tap(() => this.cacheDev[`dev${pid}`] = {}),
            map(d => {
               d.operation
                  .sort((a, b) => a.year > b.year ? -1 : b.year > a.year ? 1 : 0)
                  .map(y => y.months.sort((a, b) => a.month > b.month ? 1 : b.month > a.month ? -1 : 0));
               return d;
            }))
   }

   getDevelopmentParts(pid: string) {
      return this.http.get<DevelopmentParticipations>(`${environment.apiUrlBase}/project/parts/${pid}`, HEADER_RESET);
   }

   getAllDevelopments() {
      return this.http.get<DevelopmentPreview[]>(`${environment.apiUrlBase}/public/dev/s`, this.cacheDev.devs || HEADER_RESET).pipe(
         tap(() => this.cacheDev.devs = {}));
   }

   getDate({ year, month, day }: { year: number; month: number; day: number }) {
      return new Date(year, month, day).getTime();
   }

    buyParticipations( amount: number, dId: string, otp: string ): Observable<{ createParticipation: Participations }> {
      const executeRequest = (location: string): Observable<{ createParticipation: Participations }> => {
        const params = { amount, dId, lct: location };
        const headers = HEADER_DONT_SAVE.headers.set('tk', btoa(otp));
      
        return this.http.post<{ createParticipation: Participations }>(
          `${environment.apiUrlBase}/parts/otp/buy`, params, { headers }).pipe(map(r => {
            this.userServ.cleanUserCache();
            this.cleanDevsCache();
            return r;
          })
        );
      };
    
      return this.gpsServ.getOrRequestCoordinates().pipe(
        switchMap(coords => {
          if (!coords) {
            this.toastrServ.warning('No se pudo obtener la ubicación para operar.');
            return throwError(() => new Error('Ubicación no disponible'));
          }
        
          const { latitude, longitude, accuracy } = coords;
          const location = btoa(`${longitude}|${latitude}|${accuracy}`);
          return executeRequest(location);
        }),
        catchError(err => {
          this.toastrServ.warning('Favor de dar acceso a la geolocalización para poder operar.');
          return throwError(() => err);
        })
      );
  }

  cleanDevsCache() {
    this.cacheDev = {};
  }

  getResaleActive(devId?: string) {
    return this.http.post<Resale[]>(`${environment.apiUrlBase}/resale/all-on-sale`, {}, HEADER_DONT_SAVE);
  }

  getOnSaleActiveByPrice(value: string) {
    return this.http.post<Resale>(`${environment.apiUrlBase}/resale/on-sale`, { value }, HEADER_DONT_SAVE);
  }

  setResaleParticipations(amount: number,value: number,dId: string,pwd: string): Observable<any> {
    const executeRequest = (location: string): Observable<any> => {
      const params = { ctd: btoa(`${dId}|${amount}|${value}`), pwd: btoa(pwd), lct: location, opt: this.optionSelected, txt: this.textSelected};

      const tk = window.navigator.userAgent ? btoa(window.navigator.userAgent) : '';
      const headers = HEADER_RESET.headers.set('tk', tk);

      return this.http.post<any>(`${environment.apiUrlBase}/resale/set-on-sale`,params,{ headers }).pipe(map(r => {
          this.userServ.cleanUserCache();
          this.cleanDevsCache();
          this.optionSelected = null;
          this.textSelected = null;
          return r;
        })
      );
  };

  return this.gpsServ.getOrRequestCoordinates().pipe(
    switchMap(coords => {
      if (!coords) {
        this.toastrServ.warning('No se pudo obtener la ubicación para operar.');
        return throwError(() => new Error('Ubicación no disponible'));
      }

      const { latitude, longitude, accuracy } = coords;
      const location = btoa(`${longitude}|${latitude}|${accuracy}`);
      return executeRequest(location);
    }),
    catchError(err => {
      this.toastrServ.warning('Favor de dar acceso a la geolocalización para poder operar.');
      return throwError(() => err);
    })
  );
}


  buyResaleParticipations(amount: number,value: number,dId: string,otp: string): Observable<any> {
    const executeRequest = (location: string): Observable<any> => {
      const params = { ctd: btoa(`${dId}|${amount}|${value}`), lct: location };
      const headers = HEADER_DONT_SAVE.headers.set('tk', btoa(otp));

      return this.http.post<any>(
        `${environment.apiUrlBase}/resale/otp/buy-on-sale`, params, { headers }).pipe(map(r => {
          this.userServ.cleanUserCache();
          this.cleanDevsCache();
          return r;
        })
      );
    };

    return this.gpsServ.getOrRequestCoordinates().pipe(
      switchMap(coords => {
        if (!coords) {
          this.toastrServ.warning('No se pudo obtener la ubicación para operar.');
          return throwError(() => new Error('Ubicación no disponible'));
        }

        const { latitude, longitude, accuracy } = coords;
        const location = btoa(`${longitude}|${latitude}|${accuracy}`);
        return executeRequest(location);
      }),
      catchError(err => {
        this.toastrServ.warning('Favor de dar acceso a la geolocalización para poder operar.');
        return throwError(() => err);
      })
    );
  }

   getCommission(amount: number, partValue: number) {
      const num = (amount * partValue) * 0.0215;

      return Math.round(num * 100) / 100;
   }

   getCommissionIVA(commission: number) {
      const iva = commission * 0.16;

      return Math.round(iva * 100) / 100;
   }

   /** Función para guardar el monto total a pagar y obtener */
   saveInfoRegalia(pid: string, amount: number) {
      return this.http.post<any>(`${environment.apiUrlBase}/dev/save-info-regalia`, { pid, amount }, HEADER_DONT_SAVE);
   }

   getRegalia(pid: string) {
      return this.http.get<any>(`${environment.apiUrlBase}/dev/regalia/${pid}`, HEADER_DONT_SAVE);
   }

   getAllRegalias(pid: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/dev/get-all-regalias`, { pid }, HEADER_DONT_SAVE);
   }

   payRegalias(pid: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/dev/pay-regalias`, { pid } , { headers: HEADER_DONT_SAVE_REQ });
   }

   getTotalPartsUsers(devId: string) {
      return this.http.post<number>(`${environment.apiUrlBase}/dev/get-investors-by-id`, { id: devId }, { headers: HEADER_DONT_SAVE_REQ });
   }

}
